<template>
  <div :class="[
    'min-h-screen transition-all duration-300',
    isDark ? 'dark bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-blue-50'
  ]">
    <!-- Theme Toggle Button -->
    <div class="fixed top-4 right-4 z-50">
      <button
          @click="toggleTheme"
          :class="[
          'w-12 h-12 rounded-full shadow-xl backdrop-blur-sm border flex items-center justify-center transition-all duration-300 transform hover:scale-110 active:scale-95',
          isDark
            ? 'bg-gray-800/80 border-gray-700 text-yellow-300 hover:bg-gray-700/80'
            : 'bg-white/80 border-gray-200 text-gray-800 hover:bg-white'
        ]"
          aria-label="Toggle theme"
      >
        <svg v-if="isDark" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
        </svg>
      </button>
    </div>

    <!-- Main Container -->
    <div class="max-w-6xl mx-auto p-4 md:p-8">
      <!-- Header -->
      <div class="text-center mb-8 md:mb-12">
        <div class="relative inline-block">
          <!-- Decorative elements -->
          <div class="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg blur opacity-20 dark:opacity-30"></div>
          <div class="relative">
            <h1 :class="[
              'text-3xl md:text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r',
              isDark ? 'from-blue-400 to-purple-400' : 'from-blue-600 to-purple-600'
            ]">
              Henry Task Manager v4.0
            </h1>
          </div>
        </div>
        <p :class="[
          'text-sm md:text-base transition-colors duration-300',
          isDark ? 'text-gray-400' : 'text-gray-600'
        ]">
          Dual-language task management system
        </p>
      </div>

      <!-- Task Input Section -->
      <div :class="[
        'rounded-2xl shadow-xl p-6 mb-8 transition-all duration-300',
        isDark
          ? 'bg-gray-800/50 border border-gray-700 backdrop-blur-sm'
          : 'bg-white border border-gray-200'
      ]">
        <h2 :class="[
          'text-lg font-semibold mb-4 flex items-center gap-2 transition-colors duration-300',
          isDark ? 'text-gray-300' : 'text-gray-800'
        ]">
          <div :class="[
            'p-2 rounded-lg',
            isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-500'
          ]">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
          </div>
          Add New Task
        </h2>
        <div class="relative">
          <input
              v-model="currentTask"
              @keyup.enter="addTask"
              :class="[
              'w-full px-4 py-4 pl-12 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2',
              isDark
                ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
            ]"
              placeholder="Type your task and press Enter..."
              type="text"
          />
          <div :class="[
            'absolute left-4 top-1/2 transform -translate-y-1/2',
            isDark ? 'text-gray-500' : 'text-gray-400'
          ]">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Unified Task Board -->
      <div :class="[
        'rounded-2xl shadow-xl border overflow-hidden mb-8 transition-all duration-300',
        isDark
          ? 'bg-gray-800/50 border-gray-700'
          : 'bg-white border-gray-200'
      ]">
        <!-- Board Header -->
        <div :class="[
          'p-6',
          isDark
            ? 'bg-gradient-to-r from-gray-800 to-gray-900'
            : 'bg-gradient-to-r from-blue-600 to-purple-600'
        ]">
          <h2 class="text-xl font-bold text-white flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            Daily Tasks
          </h2>
        </div>

        <!-- Active Tasks Section -->
        <div class="p-6">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h3 :class="[
              'text-lg font-semibold flex items-center gap-2',
              isDark ? 'text-gray-300' : 'text-gray-800'
            ]">
              <div :class="[
                'p-2 rounded-lg',
                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-500'
              ]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
              Pending Tasks
              <span :class="[
                'ml-2 px-3 py-1 text-xs font-medium rounded-full',
                isDark
                  ? 'bg-blue-500/20 text-blue-300'
                  : 'bg-blue-100 text-blue-800'
              ]">
                {{ tasks.length }}
              </span>
            </h3>
            <div class="flex gap-3">
              <button
                  @click="copyTasks"
                  :class="[
                  'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                ]"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                </svg>
                Copy
              </button>
              <button
                  @click="copyTransTasks"
                  :class="[
                  'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                  isDark
                    ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300'
                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600'
                ]"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                </svg>
                复制
              </button>
            </div>
          </div>

          <div v-if="tasks.length > 0" class="space-y-4">
            <!-- Pending Task Item -->
            <div
                v-for="(task, index) in tasks"
                :key="'task-' + index"
                :class="[
                'p-5 rounded-xl border transition-all duration-300 hover:shadow-lg',
                isDark
                  ? 'bg-gray-800/30 border-gray-700 hover:border-blue-500/50'
                  : 'bg-white border-gray-200 hover:border-blue-200 hover:shadow-blue-50'
              ]"
            >
              <!-- English Task -->
              <div :class="[
                'text-lg font-medium mb-2 transition-colors duration-300',
                isDark ? 'text-gray-100' : 'text-gray-900'
              ]">
                {{ task }}
              </div>

              <!-- Chinese Translation - Edit Mode -->
              <div v-if="editingPendingIndex === index" :class="[
                'p-3 rounded-lg border transition-colors duration-300 mb-4',
                isDark
                  ? 'bg-gray-800/50 border-gray-700'
                  : 'bg-gray-50 border-gray-200'
              ]">
                <div class="flex items-center gap-2 mb-2">
                  <svg class="w-4 h-4" :class="isDark ? 'text-emerald-400' : 'text-emerald-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  <span :class="[
                    'text-xs font-medium',
                    isDark ? 'text-emerald-400' : 'text-emerald-600'
                  ]">
                    编辑中文翻译
                  </span>
                </div>
                <textarea
                    v-model="editingText"
                    :class="[
                    'w-full px-3 py-2 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 resize-none',
                    isDark
                      ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-emerald-500 focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-800 focus:ring-emerald-500 focus:border-emerald-500'
                  ]"
                    rows="3"
                    @keydown.ctrl.enter="savePendingEdit(index)"
                ></textarea>
                <div class="flex gap-2 mt-3">
                  <button
                      @click="savePendingEdit(index)"
                      :class="[
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                      isDark
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                        : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    Save
                  </button>
                  <button
                      @click="cancelEdit"
                      :class="[
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                      isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                    Cancel
                  </button>
                </div>
              </div>

              <!-- Chinese Translation - View Mode -->
              <div v-else :class="[
                'p-3 rounded-lg border transition-colors duration-300',
                isDark
                  ? 'bg-gray-800/50 border-gray-700 text-gray-300'
                  : 'bg-gray-50 border-gray-100 text-gray-600'
              ]">
                <div class="flex items-center justify-between mb-1">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4" :class="isDark ? 'text-emerald-400' : 'text-emerald-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                    </svg>
                    <span :class="[
                      'text-xs font-medium',
                      isDark ? 'text-emerald-400' : 'text-emerald-600'
                    ]">
                      中文翻译
                    </span>
                  </div>
                  <button
                      @click="editPendingTranslation(index, transTasks[index])"
                      :class="[
                      'px-2 py-1 text-xs font-medium rounded transition-all duration-200 flex items-center gap-1',
                      isDark
                        ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'
                        : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                    ]"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                    Edit
                  </button>
                </div>
                <div class="whitespace-pre-wrap">{{ transTasks[index] }}</div>
              </div>

              <!-- Actions -->
              <div class="mt-4 flex gap-3">
                <button
                    @click="taskCompleted(index)"
                    :class="[
                    'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                    isDark
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-200 hover:shadow-blue-300'
                  ]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  Complete
                </button>
                <button
                    @click="removeTask(index)"
                    :class="[
                    'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2',
                    isDark
                      ? 'bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30'
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-red-200 hover:shadow-red-300'
                  ]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                  Remove
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-10">
            <div :class="[
              'w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full',
              isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-400'
            ]">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <p :class="[
              'text-lg mb-2 transition-colors duration-300',
              isDark ? 'text-gray-400' : 'text-gray-500'
            ]">
              No pending tasks
            </p>
            <p :class="[
              'text-sm transition-colors duration-300',
              isDark ? 'text-gray-500' : 'text-gray-400'
            ]">
              Add a task using the input above
            </p>
          </div>
        </div>

        <!-- Completed Tasks Section -->
        <div v-if="compTasks.length > 0" :class="[
          'border-t p-6 transition-colors duration-300',
          isDark
            ? 'border-gray-700 bg-gray-800/30'
            : 'border-gray-200 bg-gray-50'
        ]">
          <div class="flex items-center justify-between mb-6">
            <h3 :class="[
              'text-lg font-semibold flex items-center gap-2',
              isDark ? 'text-gray-300' : 'text-gray-800'
            ]">
              <div :class="[
                'p-2 rounded-lg',
                isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-500'
              ]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              Completed Tasks
              <span :class="[
                'ml-2 px-3 py-1 text-xs font-medium rounded-full',
                isDark
                  ? 'bg-green-500/20 text-green-300'
                  : 'bg-green-100 text-green-800'
              ]">
                {{ compTasks.length }}
              </span>
            </h3>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Completed Task Item -->
            <div
                v-for="(task, index) in compTasks"
                :key="'done-' + index"
                :class="[
                'p-4 rounded-xl border transition-all duration-300',
                isDark
                  ? 'bg-gray-800/50 border-green-800/30 hover:border-green-500/50'
                  : 'border-green-200 bg-green-50/50 hover:bg-green-50 hover:border-green-300'
              ]"
            >
              <div class="flex items-start gap-3">
                <!-- Checkmark Badge -->
                <div :class="[
                  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1',
                  isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                ]">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>

                <!-- Task Content -->
                <div class="flex-1">
                  <!-- English Task -->
                  <div :class="[
                    'font-medium mb-2 transition-colors duration-300',
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  ]">
                    {{ task }}
                  </div>

                  <!-- Chinese Translation - Edit Mode -->
                  <div v-if="editingCompletedIndex === index" :class="[
                    'mb-3 transition-colors duration-300',
                  ]">
                    <div class="flex items-center gap-2 mb-2">
                      <svg class="w-4 h-4" :class="isDark ? 'text-emerald-400' : 'text-emerald-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                      <span :class="[
                        'text-xs font-medium',
                        isDark ? 'text-emerald-400' : 'text-emerald-600'
                      ]">
                        编辑中文翻译
                      </span>
                    </div>
                    <textarea
                        v-model="editingText"
                        :class="[
                        'w-full px-3 py-2 text-sm rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 resize-none',
                        isDark
                          ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-emerald-500 focus:border-emerald-500'
                          : 'bg-white border-gray-300 text-gray-800 focus:ring-emerald-500 focus:border-emerald-500'
                      ]"
                        rows="2"
                        @keydown.ctrl.enter="saveCompletedEdit(index)"
                    ></textarea>
                    <div class="flex gap-2 mt-2">
                      <button
                          @click="saveCompletedEdit(index)"
                          :class="[
                          'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5',
                          isDark
                            ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                        ]"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        Save
                      </button>
                      <button
                          @click="cancelEdit"
                          :class="[
                          'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5',
                          isDark
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        ]"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        Cancel
                      </button>
                    </div>
                  </div>

                  <!-- Chinese Translation - View Mode -->
                  <div v-else :class="[
                    'text-sm mb-3 transition-colors duration-300',
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  ]">
                    <div class="flex items-center justify-between gap-1.5 mb-1">
                      <div class="flex items-center gap-1.5">
                        <span :class="[
                          'text-xs font-medium',
                          isDark ? 'text-emerald-400' : 'text-emerald-500'
                        ]">
                          中文:
                        </span>
                        <span class="whitespace-pre-wrap">{{ compTransTasks[index] }}</span>
                      </div>
                      <button
                          @click="editCompletedTranslation(index, compTransTasks[index])"
                          :class="[
                          'px-2 py-1 text-xs rounded transition-all duration-200 flex items-center gap-1',
                          isDark
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'
                            : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                        ]"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                        Edit
                      </button>
                    </div>
                  </div>

                  <!-- Revert Button -->
                  <button
                      @click="returnTask(index)"
                      :class="[
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5',
                      isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-600 hover:bg-gray-700 text-white'
                    ]"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                    </svg>
                    Revert
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Section -->
      <div :class="[
        'rounded-2xl shadow-xl p-6 mb-8 transition-all duration-300',
        isDark
          ? 'bg-gradient-to-r from-gray-800 to-gray-900'
          : 'bg-gradient-to-r from-gray-800 to-gray-900'
      ]">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center space-x-6">
            <div class="text-center">
              <div class="text-3xl font-bold text-white">{{ tasks.length }}</div>
              <div class="text-gray-300 text-sm">Pending Tasks</div>
            </div>
            <div class="h-8 w-px bg-gray-700"></div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white">{{ compTasks.length }}</div>
              <div class="text-gray-300 text-sm">Completed Tasks</div>
            </div>
            <div class="h-8 w-px bg-gray-700"></div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white">{{ tasks.length + compTasks.length }}</div>
              <div class="text-gray-300 text-sm">Total Tasks</div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>

    <!-- Floating Add Button (Mobile) -->
    <div class="lg:hidden fixed bottom-6 right-6 z-40">
      <button
          @click="document.querySelector('input')?.focus()"
          :class="[
          'w-14 h-14 rounded-full shadow-2xl backdrop-blur-sm border flex items-center justify-center transition-all duration-300 transform hover:scale-110 active:scale-95',
          isDark
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-500/30'
            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-blue-300'
        ]"
          aria-label="Add task"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Footer from './components/FooterComponent.vue'

export default {
  components: { Footer }, // TaskInput
  data() {
    return {
      isDark: false,
      canSave: false,
      currentTask: '',
      tasks: [],
      compTasks: [],
      transTasks: [],
      compTransTasks: [],
      // Edit state
      editingPendingIndex: null,
      editingCompletedIndex: null,
      editingText: '',
      originalText: ''
    }
  },
  methods: {
    addTask() {
      if (!this.currentTask) return

      const task = this.currentTask
      this.tasks.push(task)
      this.currentTask = ''

      axios.get('https://api.mymemory.translated.net/get', {
        params: {
          langpair: 'en|zh',
          q: task
        }
      }).then(res => {
        if (res.data && res.data.responseData) {
          this.transTasks.push(
              res.data.responseData.translatedText
          )
        }
      }).catch(err => {
        console.error(err)
        // fallback if translation fails
        this.transTasks.push(task)
      })
    },
    taskCompleted(index) {
      this.compTasks.push(this.tasks[index])
      this.compTransTasks.push(this.transTasks[index])

      this.tasks.splice(index, 1)
      this.transTasks.splice(index, 1)
    },
    removeTask(index) {
      this.tasks.splice(index, 1)
      this.transTasks.splice(index, 1)
    },
    returnTask(index) {
      this.tasks.push(this.compTasks[index])
      this.transTasks.push(this.compTransTasks[index])

      this.compTasks.splice(index, 1)
      this.compTransTasks.splice(index, 1)
    },
    copyTasks() {
      let text = ''
      let count = 1

      text += 'Completed:\n'
      for (const task of this.compTasks) {
        text += count + '. ' + task + ', completed\n'
        count++
      }

      count = 1
      text += 'Need to Complete:\n'
      for (const task of this.tasks) {
        text += count + '. ' + task + '\n'
        count++
      }

      navigator.clipboard.writeText(text).catch(err => {
        console.error('Copy failed:', err)
      })
    },
    copyTransTasks() {
      let text = ''
      let count = 1

      text += '今日完成工作:\n'
      for (const task of this.compTransTasks) {
        text += count + '. ' + task + ', 完整的\n'
        count++
      }

      count = 1
      text += '明日工作:\n'
      for (const task of this.transTasks) {
        text += count + '. ' + task + '\n'
        count++
      }

      navigator.clipboard.writeText(text).catch(err => {
        console.error('Copy failed:', err)
      })
    },
    saveTasks() {
      if(!this.canSave) return;
      const info = {
        tasks: this.tasks,
        compTasks: this.compTasks,
        transTasks: this.transTasks,
        compTransTasks: this.compTransTasks,
      }
      console.log("SAVING", info);
      localStorage.setItem('tasks', JSON.stringify(info))
    },
    toggleTheme(){
      this.isDark = !this.isDark;
      // Save to localStorage
      localStorage.setItem('taskManagerTheme', this.isDark ? 'dark' : 'light')

      // Apply theme to document
      if (this.isDark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    },
    initTheme() {
      const savedTheme = localStorage.getItem('taskManagerTheme')
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

      this.isDark = savedTheme ? savedTheme === 'dark' : prefersDark;

      if (this.isDark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    },
    // Edit methods
    editPendingTranslation(index, text) {
      this.editingPendingIndex = index
      this.editingCompletedIndex = null
      this.editingText = text
      this.originalText = text
    },
    editCompletedTranslation(index, text) {
      this.editingCompletedIndex = index
      this.editingPendingIndex = null
      this.editingText = text
      this.originalText = text
    },
    savePendingEdit(index) {
      if (this.editingPendingIndex === null || index !== this.editingPendingIndex) return;
      this.transTasks[index] = this.editingText;
      this.cancelEdit();
    },
    saveCompletedEdit(index) {
      if (this.editingCompletedIndex === null || index !== this.editingCompletedIndex) return;
      this.compTransTasks[index] = this.editingText;
      this.cancelEdit();
    },
    cancelEdit() {
      this.editingPendingIndex = null
      this.editingCompletedIndex = null
      this.editingText = ''
      this.originalText = ''
    },
    enableSave(canSave=true) {
      setTimeout(() => {
        this.canSave = canSave;
        console.log("Can Save Now!");
      }, 1000);
    },
  },
  watch: {
    tasks: {
      handler() { this.saveTasks() },
      deep: true
    },
    compTasks: {
      handler() { this.saveTasks() },
      deep: true
    },
    transTasks: {
      handler() { this.saveTasks() },
      deep: true
    },
    compTransTasks: {
      handler() { this.saveTasks() },
      deep: true
    },
  },
  mounted() {
    this.canSave = false;
    this.initTheme();
    const saved = localStorage.getItem('tasks');
    if(!saved) {
      this.enableSave();
      return;
    }
    try {
      const data = JSON.parse(saved);
      console.log("DATA", data);
      this.tasks = data.tasks || [];
      this.transTasks = data.transTasks || [];
      this.compTasks = data.compTasks || [];
      this.compTransTasks = data.compTransTasks || [];
    } catch (e) {
      console.error('Failed to parse saved tasks', e);
    } finally {
      this.enableSave();
    }
  }
}
</script>